import i18next from 'i18next';
import {
  API_REQUEST_RETRY_INTERVAL,
  CLOUD_FILES_URL,
  HttpMethod,
  HttpStatusCode,
  READYSTATE_DONE,
  TIMING_EVENT,
  TEMPLATES_URL,
  ABORT_ERROR_NAME,
} from 'appConstants';
import {
  store,
  setRequestRetry,
  setRequestSuccess,
  setRequestStart,
  setRequestError,
  setAbortError,
} from 'reducers';
import { ImmutableQuery } from 'services';
import { AttachmentErrors } from 'services';
import { proxyFetch } from '../fetch';
import 'abortcontroller-polyfill/dist/abortcontroller-polyfill-only';
import { getProxyFetchArgs } from '@rss-engineering/fabric-ui-session';
import { Attachment, AttachmentAWS, SentryManualError } from 'types';
import { MiscUtils } from './MiscUtils';
import { FeatureFlags } from './FeatureFlags';
import 'abortcontroller-polyfill/dist/abortcontroller-polyfill-only';
import { isRacker } from '@rss-engineering/fabric-ui-session';
import {
  ACCOUNTS_URL,
  RESOURCES_URL,
  CONTACTS_URL,
  CONTACT_GROUPS_URL,
  CLOUD_GROUPS_URL,
  ALL_CONTACTS_URL,
} from '../appConstants/ApiRequest';

interface RawRequestError {
  faultCode?: string;
  message: string;
  moreInfo?: string;
  description?: string;
  status?: number;
  transactionId?: string;
}

/** Encapsulates potential API errors caused by failed requests or otherwise. */
class ApiRequestError extends Error implements RawRequestError {
  faultCode?: string;
  message: string;
  moreInfo?: string;
  status?: number;
  transactionId?: string;

  constructor(jsonResponse: RawRequestError = {} as RawRequestError) {
    const {
      faultCode = '',
      message = '',
      moreInfo = '',
      description = '',
      status = 0,
      transactionId = '',
    } = jsonResponse;

    super(`${status ? status + ': ' : ''}${message}`);
    this.faultCode = faultCode;
    this.message = message || description;
    this.moreInfo = moreInfo || JSON.stringify(jsonResponse);
    this.status = status;
    this.transactionId = transactionId;
  }
}

export interface RequestOptions {
  /**
   * If included, this request will make dispatches using this id to update the
   * global Redux RequestManager state. Updated on start, retry, error, and
   * success
   */
  dispatchId?: string;

  /** Additional headers to use when making the request */
  headers?: { [key: string]: string };

  /** A function to be executed if request and/or retry result in an error */
  onError?: (error: {}) => void;

  /** A function to be executed just before request is attempted */
  onPreRequest?: () => void;

  /**
   * A function to be executed each time request attempts a retry.
   * Callback is provided a method that will cancel the current delay and make
   * the next attempt immediately when executed.
   */
  onRetry?: (retryNow: () => void, wait: number) => void;

  /** A function to be executed when the request is successful */
  onSuccess?: (response: {}, responseHeaders: Headers) => void;

  /**
   * A function to be executed during after any request state dispatch. Must be
   * used in conjunction with dispatch ID, as it is only useful as a means to
   * retrive request state from Redux global app state from a non-connected
   * React component.
   */
  onUpdate?: (dispatchId: string) => void;

  /**
   * The number of times the request will be retried before finally throwing an
   * error. Defaults to 0. If unspecified, requests are made once with no retry.
   *
   * Each request will have a delay increasing by 5 seconds each retry, i.e. 5,
   * then 10, then 15, etc.
   */
  retries?: number;

  /**
   * Takes the raw http response and applies a transformed state prior to
   * resolving. Request will use transformed response for onSuccess and return.
   */
  transform?: (response: {}) => {};
}

/** Promise which may be cancelled prior to completion; used for retries */
interface CancellablePromise extends Promise<{}> {
  cancel: () => void;
}

/**
 * Creates a promise that will resolve after the specified duration and cache
 * to a local dict with capability to end delay early
 */
const delay = (ms: number): CancellablePromise => {
  let resolveFn: Function;
  let timeoutId: number;

  const promise = new Promise((resolve) => {
    resolveFn = resolve;
    timeoutId = window.setTimeout(resolve, ms || 0);
  });

  (promise as CancellablePromise).cancel = () => {
    resolveFn();
    clearTimeout(timeoutId);
  };

  return promise as CancellablePromise;
};

type Payload = {
  [key: string]: string;
};

export const updatePayloadAndUrl = (
  method: HttpMethod,
  url: string,
  payload: Payload | undefined,
) => {
  const updatedPayload = { ...payload };
  let updatedUrl = url;

  if (!isRacker()) {
    return { updatedPayload, updatedUrl };
  }

  // checks whether any of the values in array exist in url
  const includesAny = (urls: string[]) =>
    MiscUtils.includesAny(urls, updatedUrl);

  // should append with accountId in payload/urlParams
  const accountIdUrls = [CONTACT_GROUPS_URL, CONTACTS_URL, ALL_CONTACTS_URL];

  // should append with domainId in payload/urlParams
  const domainIdUrls = [ACCOUNTS_URL, CLOUD_GROUPS_URL];

  // should append with tenantId in payload/urlParams
  const tenantIdUrls = [RESOURCES_URL];

  const ticketsParams = ['searchInComments', 'status', 'export'];

  if (method === HttpMethod.GET) {
    if (includesAny(accountIdUrls)) {
      updatedUrl += `?accountID=${window.PORTAL_DATA.tenants[0]}`;
    } else if (includesAny(domainIdUrls)) {
      updatedUrl += `?domainID=${window.PORTAL_DATA.domainId}`;
    } else if (includesAny(tenantIdUrls)) {
      updatedUrl += `&tenantID=${window.PORTAL_DATA.tenants[0]}`;
    } else if (includesAny(ticketsParams)) {
      const urlSplit = updatedUrl.split('?');

      // existing Url WithoutParams and QueryParams
      const urlWithoutParams = urlSplit[0];
      const existingQueryParams = ImmutableQuery.convertToObject(urlSplit[1]);

      // Adding domainId to existing Params and converting it to a query string
      const updatedQueryParams = {
        domainID: window.PORTAL_DATA.domainId,
        ...existingQueryParams,
      };
      const updatedQueryString = ImmutableQuery.fromObject(
        updatedQueryParams,
      ).toQueryString();

      updatedUrl = `${urlWithoutParams}${updatedQueryString}`;
    }
  } else {
    if (includesAny(accountIdUrls) || includesAny(ticketsParams)) {
      // @ts-ignore
      updatedPayload.accountID = window.PORTAL_DATA.tenants[0];
    } else if (includesAny(tenantIdUrls)) {
      // @ts-ignore
      updatedPayload.tenantID = window.PORTAL_DATA.tenants[0];
    }
  }

  return { updatedPayload, updatedUrl };
};

/**
 * Make API Request using cached configuration, returning a parsed JSON object
 */
class ApiRequest {
  private defaultHeaders = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };

  /**
   * private requestConfig = {
   *  credentials: 'same-origin' as RequestCredentials,
   * };
   */

  /** Determine if object is an ApiRequestError */
  isError(obj: {}): obj is ApiRequestError {
    return obj instanceof Error;
  }

  /** Makes GET request to the specified URL */
  get(url: string, options?: RequestOptions) {
    const data = updatePayloadAndUrl(HttpMethod.GET, url, {});
    return this.makeRequest(HttpMethod.GET, data.updatedUrl, options);
  }

  /** Makes DELETE request to the specified URL */
  delete(url: string, options?: RequestOptions) {
    return this.makeRequest(HttpMethod.DELETE, url, options);
  }

  /** Makes PATCH request to the specified URL using the given payload */
  patch(url: string, payload: {}, options?: RequestOptions) {
    return this.makeRequest(HttpMethod.PATCH, url, options, payload);
  }

  /** Makes PUT request to the specified URL using the given payload */
  put(url: string, payload: {}, options?: RequestOptions) {
    return this.makeRequest(HttpMethod.PUT, url, options, payload);
  }

  /** Makes POST request to the specified URL using the given payload */
  post(url: string, payload: {}, options?: RequestOptions) {
    const data = updatePayloadAndUrl(HttpMethod.POST, url, payload);
    return this.makeRequest(HttpMethod.POST, url, options, data.updatedPayload);
  }

  /** Uploads a file with callback on upload progress event */
  async upload(
    attachment: Attachment | AttachmentAWS,
    body: FormData,
    url: string,
    onUpdate: (attachment: Attachment | AttachmentAWS) => void,
    isAWS = false,
    headers?: { [key: string]: string },
  ) {
    const uploadUrl = !isAWS ? CLOUD_FILES_URL : '';
    const [proxyURLWithEncodedReqURL, fetchOptions] = await getProxyFetchArgs(
      uploadUrl + `${url}`,
      {},
    );

    const xhr = new XMLHttpRequest();
    xhr.open(HttpMethod.POST, proxyURLWithEncodedReqURL, true);

    if (fetchOptions.headers) {
      Object.keys(fetchOptions.headers).forEach((key) => {
        if (key.toLowerCase() !== 'content-type') {
          xhr.setRequestHeader(key, fetchOptions.headers[key]);
        }
      });
    }

    const onProgress = (e: ProgressEvent) => {
      if (e.lengthComputable) {
        const progress = (e.loaded / e.total * 100).toFixed();
        onUpdate({ ...attachment, status: { progress } });
      }
    };
    const onFailure = (e: ProgressEvent) => {
      onUpdate({
        ...attachment,
        errors: new Set([AttachmentErrors.UPLOAD_FAILURE]),
      });
    };

    xhr.upload.addEventListener('progress', onProgress);
    xhr.upload.addEventListener('error', onFailure);
    xhr.upload.addEventListener('abort', onFailure);

    xhr.onreadystatechange = () => {
      const { readyState, status, response } = xhr;
      const isComplete =
        readyState === READYSTATE_DONE &&
        (status === HttpStatusCode.CREATED || status === HttpStatusCode.OK);

      if (isComplete) {
        onUpdate({
          ...attachment,
          errors: new Set([]),
          status: { complete: true },
          ...(isAWS && {
            data:
              (JSON.parse(response).files && JSON.parse(response).files[0]) ||
              {},
          }),
        });
      }
    };

    xhr.send(body);
  }

  /**
   * Handles logic for executing API call and applying config, attempts, etc.
   */
  private async makeRequest(
    method: HttpMethod,
    url: string,
    options: RequestOptions = {},
    payload?: {},
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  ): Promise<any> {
    let wait = 0;

    // Prepare defaults so checking if callback are defined is not needed
    const {
      dispatchId = '',
      headers = {},
      onError = MiscUtils.noop,
      onRetry = MiscUtils.noop,
      onSuccess = MiscUtils.noop,
      onUpdate = MiscUtils.noop,
      onPreRequest = MiscUtils.noop,
      retries = 0,
      transform = MiscUtils.identity,
    } = options;

    // Prepare default Error
    let error: ApiRequestError = {
      message: i18next.t('common:defaultErrorMessage'),
      name: 'DEFAULT_API_ERROR',
      status: HttpStatusCode.BAD_REQUEST,
    };

    if (
      /comments/.test(url) ||
      (FeatureFlags.isActive('federated-recipients') &&
        /contacts(\/query)?/.test(url))
    ) {
      this.defaultHeaders.Accept = 'application/vnd.ticketing.v3+json';
    } else if (/resources\/query/.test(url)) {
      this.defaultHeaders.Accept = 'application/vnd.resource.v2+json';
    } else {
      this.defaultHeaders.Accept = 'application/json';
    }

    // Clone to not mutate original
    const requestConfig = {
      // ...this.requestConfig,
      headers: { ...this.defaultHeaders },
    };

    // Append any extra headers
    Object.keys(headers).forEach(
      (key) => (requestConfig.headers[key] = headers[key]),
    );

    const browserLanguage = MiscUtils.browserLanguage(navigator);
    // Append any language header
    requestConfig.headers['Accept-Language'] = browserLanguage;

    const abortController = new AbortController();
    const { signal } = abortController; // get signal

    // Create composite config options
    const updateConfig = Object.assign(
      { method: method },
      method !== HttpMethod.GET && method !== HttpMethod.DELETE
        ? { body: JSON.stringify(payload) }
        : {},
      requestConfig,
      { signal: signal },
    );

    // Strip out potential trailing slash
    url = url.replace(/\/$/, '');

    // Attempt API request, looping `retries` number of time if failed
    for (let i = 0; i < retries + 1; i++) {
      try {
        // If not the first pass, then API request previously failed
        if (i > 0) {
          // Delay increases by 5 seconds each loop (configurable)
          const delayPromise = delay(wait);

          // Update Redux global state that request is retrying
          if (dispatchId) {
            store.dispatch(
              setRequestRetry(dispatchId, wait / 1000, delayPromise.cancel),
            );

            // On update
            onUpdate(dispatchId);
          }

          // Execute the callback before starting delay
          onRetry(delayPromise.cancel, wait / 1000);

          // Wait for the delay
          await delayPromise;
        }

        // Update Redux global state that request is starting
        if (dispatchId) {
          store.dispatch(setRequestStart(dispatchId, abortController));
          onUpdate(dispatchId);
        }

        onPreRequest();

        const startTime = Date.now();
        const rawResponse = await proxyFetch(url, updateConfig);
        const { ok, status, headers: responseHeaders } = rawResponse;

        const { pathname } = new URL(url, window.location.href);

        const responseContentType =
          responseHeaders && responseHeaders.get('content-type');

        store.dispatch({
          type: TIMING_EVENT,
          method,
          url: pathname,
          time: Date.now() - startTime,
        });

        const emptyOk =
          status === HttpStatusCode.NO_CONTENT ||
          status === HttpStatusCode.ACCEPTED;

        /**
         * If the API fails and the response type is not a JSON,
         * we'll be throwing the default error with the key
         * `moreInfo` to have the raw response from the API.
         *
         * Before this change any failure with non-json response type
         * just goes to the catch statement without any proper details.
         */

        if (
          !ok &&
          responseContentType &&
          !!responseContentType.indexOf('application/json')
        ) {
          const rawJsonResponse = await rawResponse.json();
          if (rawJsonResponse.error) {
            error = rawJsonResponse.error;
          } else {
            error.moreInfo = await rawResponse.text();
          }
          if (status === HttpStatusCode.INTERNAL_SERVER_ERROR) {
            error.message = i18next.t('common:defaultErrorMessage');
          }
          error.status = status;
          throw new ApiRequestError(error as RawRequestError);
        }

        let jsonResponse = emptyOk
          ? ''
          : responseContentType &&
            !responseContentType.indexOf('application/octet-stream')
            ? await rawResponse.blob()
            : await rawResponse.json();

        /* 
         * Templates call gives 409 when all the devices are offline.
         * Instead of showing error given by the api we are passing it as success
         * as we have a condition to create ticket with offline device.
         */
        const allOfflineDevicesCondition =
          status === HttpStatusCode.CONFLICT && url === TEMPLATES_URL;

        // API request succeeded
        if (ok || allOfflineDevicesCondition) {
          const formattedResponse = transform(jsonResponse);
          /**
           * Need the call to `onSuccess` to happen first to ensure handling
           * before any tasks which are checking the request status.
           */
          onSuccess(formattedResponse, responseHeaders);

          // Update Redux global state that request succeeded
          if (dispatchId) {
            store.dispatch(setRequestSuccess(dispatchId));
            onUpdate(dispatchId);
          }

          return formattedResponse;
        }

        throw new ApiRequestError(jsonResponse as RawRequestError);
      } catch (err) {
        error = err as Error;

        // Override default error if it is from the API
        if (err.transactionId) {
          error = err as ApiRequestError;
        }

        // If forbidden or rate exceeded or request too large, don't bother retrying
        if (
          error.status === HttpStatusCode.FORBIDDEN ||
          error.status === HttpStatusCode.RATE_LIMIT ||
          error.status === HttpStatusCode.REQUEST_TOO_LARGE
        ) {
          break;
        }

        wait += API_REQUEST_RETRY_INTERVAL;
      }
    }

    // Update Redux global state that request errored
    if (dispatchId) {
      if (error.name === ABORT_ERROR_NAME) {
        store.dispatch(setAbortError(dispatchId, error));
      } else {
        store.dispatch(setRequestError(dispatchId, error));
      }
      onUpdate(dispatchId);
    }

    onError(error);

    const userId = window.PORTAL_DATA
      ? window.PORTAL_DATA.userId
      : 'USER_NOT_LOGGED_IN';
    const domainId = window.PORTAL_DATA
      ? window.PORTAL_DATA.domainId
      : 'USER_NOT_LOGGED_IN';
    // Sentry log to capture errors
    MiscUtils.logSentryErrorManually({
      name: 'API Request Error',
      description: `API endpoint ${url}`,
      extraData: {
        error: error.message,
        moreInfo: error.moreInfo,
        url: url,
        payload: JSON.stringify(payload),
        apiTransactionId: error.transactionId,
      },
      fingerprint: [
        userId,
        MiscUtils.isUserImpersonated() ? 'customer' : 'racker',
        url,
        JSON.stringify(payload),
      ],
      tags: {
        userId: userId,
        domainId: domainId,
        isUserImpersonated: MiscUtils.isUserImpersonated(),
      },
    } as SentryManualError);

    return error;
  }
}

// Export an instance to use as a Singleton
const ApiRequestSingleton = new ApiRequest();

export { ApiRequestSingleton as ApiRequest, ApiRequestError };
